import { ThemeEvents } from "@theme/events";

/**
 * Cart "buy N spices, get one free" progress banner.
 *
 * Deliberately independent from tin-meter.js — separate custom element,
 * separate counting/update logic, separate markup. The only thing shared
 * with the tin-meter component is the underlying settings that configure
 * the promotion (enable_12th_item_offer, tin_meter_collection), which are
 * read server-side in snippets/cart-reward-progress.liquid, not here.
 */
class CartRewardProgress extends HTMLElement {
  #observer;
  #requestToken = 0;

  connectedCallback() {
    this.eligibleVariantIds = new Set(this.#parseVariantIds());
    this.goal = Number(this.dataset.goal || 12);

    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);

    // Section morphs can replace the Liquid-rendered progress after cart:update.
    // Refresh from the full cart when that happens, including attribute changes.
    this.#observer = new MutationObserver(() => this.#fetchAndUpdate());
    this.#observer.observe(this, {
      childList: true,
      subtree: true,
      attributes: true,
      characterData: true,
    });

    this.#fetchAndUpdate();
  }

  disconnectedCallback() {
    document.removeEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
    this.#observer?.disconnect();
  }

  #parseVariantIds() {
    try {
      return JSON.parse(this.dataset.eligibleVariantIds || "[]").map(String);
    } catch {
      return [];
    }
  }

  #onCartUpdate = (event) => {
    // Invalidate any fetch already in flight (e.g. the initial one kicked
    // off from connectedCallback) so it can't resolve later and overwrite
    // this more current state with stale data.
    this.#requestToken++;

    const cart = event.detail?.resource;
    // Add responses contain only the added lines; only full carts have item_count.
    if (Array.isArray(cart?.items) && Number.isFinite(cart.item_count)) {
      this.#update(cart);
      return;
    }
    this.#fetchAndUpdate();
  };

  async #fetchAndUpdate() {
    const token = ++this.#requestToken;
    try {
      const response = await fetch("/cart.js", { cache: "no-store" });
      if (!response.ok) return;
      const cart = await response.json();
      // A newer request (or a synchronous cart:update) has since started;
      // this response is stale, so don't let it clobber a fresher result.
      if (token !== this.#requestToken) return;
      this.#update(cart);
    } catch {
      // Keep the current state if the cart cannot be retrieved.
    }
  }

  #update(cart) {
    // Re-query children fresh rather than caching them from
    // connectedCallback, since a morph can replace them with new nodes.
    const subtitle = this.querySelector("[data-cart-reward-subtitle]");
    const fill = this.querySelector("[data-cart-reward-fill]");
    const progressbar = this.querySelector("[data-cart-reward-progressbar]");

    const count = (cart.items || []).reduce((total, item) => {
      const isBundleChild = Boolean(item.properties?._parent_bundle_id);
      return !isBundleChild && this.eligibleVariantIds.has(String(item.variant_id))
        ? total + Number(item.quantity || 0)
        : total;
    }, 0);
    const complete = count >= this.goal;
    const remaining = Math.max(this.goal - count, 0);
    const percent = Math.min((count / this.goal) * 100, 100);

    // The writes below (setAttribute / textContent) would otherwise
    // re-trigger the attributes/childList observer set up in
    // connectedCallback and loop forever. Pause it for this one write.
    this.#observer?.disconnect();

    this.classList.toggle("cart-reward-progress--complete", complete);

    if (fill) fill.style.width = `${percent}%`;
    if (progressbar) progressbar.setAttribute("aria-valuenow", String(Math.min(count, this.goal)));
    if (subtitle) {
      subtitle.textContent = complete
        ? "Yay! You got 12 spices for the price of 11 ✨"
        : count === 0
          ? "You're yet to add spices to your cart"
          : `${remaining} more ${remaining === 1 ? "spice" : "spices"} to go!`;
    }

    this.#observer?.observe(this, {
      childList: true,
      subtree: true,
      attributes: true,
      characterData: true,
    });
  }
}

if (!customElements.get("cart-reward-progress")) {
  customElements.define("cart-reward-progress", CartRewardProgress);
}
